// Administrace uživatelů (pouze pro roli admin)
// Vzorováno na materials-admin.js – seznam + modály pro create/edit/reset/delete.
import { icon } from './icons.js';

class UsersAdmin {
  constructor() {
    this.baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:1234/api'
      : '/api';
    this.users = [];
    this._loaded = false;
  }

  // Získat informace o roli aktuálního uživatele
  getCurrentUserRole() {
    try {
      const userData = localStorage.getItem('currentUser');
      if (userData) {
        return JSON.parse(userData).role;
      }
    } catch (error) {
      console.error('Chyba při načítání role uživatele:', error);
    }
    return null;
  }

  isAdmin() {
    return this.getCurrentUserRole() === 'admin';
  }

  getAuthHeaders() {
    // SEC-7: autentizaci nese httpOnly cookie (credentials: 'include').
    // Legacy Bearer token z localStorage už nepoužíváme.
    return {};
  }

  // Voláno při přepnutí na sekci "Uživatelé"
  async showSection() {
    if (!this.isAdmin()) {
      this.showError('Nemáte oprávnění zobrazit správu uživatelů.');
      return;
    }
    await this.loadUsers();
    this.renderUsers();
  }

  async loadUsers() {
    const container = document.getElementById('users-container');
    if (container) {
      container.innerHTML = `
        <div class="flex justify-center p-8">
          ${icon('spinner', 'animate-spin h-8 w-8 text-blue-500', { role: 'status' })}
          <span class="sr-only">Načítám...</span>
        </div>`;
    }
    try {
      const response = await fetch(`${this.baseUrl}/users`, {
        headers: { ...this.getAuthHeaders() },
        credentials: 'include'
      });
      const result = await response.json();
      if (response.ok && result.success) {
        this.users = Array.isArray(result.data) ? result.data : [];
        this._loaded = true;
      } else {
        throw new Error(result.message || 'Nepodařilo se načíst uživatele');
      }
    } catch (error) {
      console.error('Chyba při načítání uživatelů:', error);
      this.users = [];
      if (container) {
        container.innerHTML = `
          <div class="bg-red-50 border border-red-200 rounded-md p-4 text-sm text-red-800">
            Chyba při načítání uživatelů: ${error.message}
          </div>`;
      }
    }
  }

  roleLabel(role) {
    return role === 'admin' ? 'Administrátor' : 'Obchodník';
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  renderUsers() {
    const container = document.getElementById('users-container');
    if (!container) return;

    if (!this.users.length) {
      container.innerHTML = `
        <div class="text-center py-12">
          ${icon('users', 'mx-auto h-12 w-12 text-ink-faint mb-3')}
          <h3 class="text-base font-medium text-ink-muted mb-1">Zatím žádní uživatelé</h3>
          <p class="text-sm text-ink-faint">Začněte přidáním uživatele tlačítkem „Přidat uživatele".</p>
        </div>`;
      return;
    }

    let rows = '';
    this.users.forEach(user => {
      const name = this.escapeHtml(user.name);
      const username = this.escapeHtml(user.username);
      const email = this.escapeHtml(user.email || '');
      const mustBadge = user.mustChangePassword
        ? `<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-yellow-50 text-yellow-800 border border-yellow-200 text-[11px] font-medium">musí změnit heslo</span>`
        : '';
      const roleBadge = user.role === 'admin'
        ? `<span class="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-medium">Administrátor</span>`
        : `<span class="inline-flex items-center px-2 py-0.5 rounded bg-ink-50 text-ink-muted text-xs font-medium">Obchodník</span>`;
      const id = this.escapeHtml(user.id);
      rows += `
        <tr class="hover:bg-surface-1 transition-colors">
          <td class="px-4 py-2 whitespace-nowrap font-medium text-ink">${name} ${mustBadge}</td>
          <td class="px-4 py-2 text-ink-muted">${username}</td>
          <td class="px-4 py-2 text-ink-muted">${email || '<span class="text-ink-faint">—</span>'}</td>
          <td class="px-4 py-2">${roleBadge}</td>
          <td class="px-4 py-2 text-right">
            <div class="flex justify-end gap-1">
              <button class="p-1.5 rounded-md hover:bg-brand-50 text-ink-muted hover:text-brand transition-colors" onclick="usersAdmin.editUser('${id}')" title="Upravit">
                ${icon('pencil-square', 'w-4 h-4')}
              </button>
              <button class="p-1.5 rounded-md hover:bg-yellow-50 text-ink-muted hover:text-yellow-700 transition-colors" onclick="usersAdmin.resetPassword('${id}')" title="Reset hesla">
                ${icon('key', 'w-4 h-4')}
              </button>
              <button class="p-1.5 rounded-md hover:bg-danger-soft text-ink-muted hover:text-danger transition-colors" onclick="usersAdmin.deleteUser('${id}')" title="Smazat">
                ${icon('trash', 'w-4 h-4')}
              </button>
            </div>
          </td>
        </tr>`;
    });

    container.innerHTML = `
      <div class="overflow-x-auto border border-ink-100 rounded-lg">
        <table class="min-w-full text-sm">
          <thead>
            <tr class="bg-surface-1 text-ink-muted">
              <th class="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider">Jméno</th>
              <th class="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider">Uživatelské jméno</th>
              <th class="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider">Email</th>
              <th class="px-4 py-2 text-left text-[11px] font-semibold uppercase tracking-wider">Role</th>
              <th class="px-4 py-2 text-right text-[11px] font-semibold uppercase tracking-wider">Akce</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-ink-50">${rows}</tbody>
        </table>
      </div>`;
  }

  // ============================================================
  // Přidání uživatele
  // ============================================================
  showAddUserModal() {
    if (!document.getElementById('addUserModal')) {
      this.createAddUserModal();
    }
    const form = document.getElementById('addUserForm');
    if (form) form.reset();
    new TailwindModal('addUserModal').show();
  }

  createAddUserModal() {
    const modalHtml = `
      <div class="fixed inset-0 z-50 overflow-y-auto hidden" id="addUserModal" aria-labelledby="addUserModalLabel" aria-hidden="true">
        <div class="flex items-center justify-center min-h-screen px-4">
          <div class="fixed inset-0 bg-black opacity-50" id="modal-backdrop"></div>
          <div class="bg-white rounded-lg shadow-xl max-w-lg w-full relative">
            <div class="px-6 py-4 border-b border-gray-200">
              <h3 class="text-lg font-medium text-gray-900" id="addUserModalLabel">Přidat uživatele</h3>
              <button type="button" class="absolute top-4 right-4 text-gray-400 hover:text-gray-600" id="modal-close-btn">
                ${icon('x-mark', 'h-6 w-6')}
              </button>
            </div>
            <div class="px-6 py-4">
              <form id="addUserForm">
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Uživatelské jméno <span class="text-red-500">*</span></label>
                  <input type="text" id="addUserUsername" required minlength="3" maxlength="50" autocomplete="off" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="napr. jnovak">
                </div>
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Celé jméno <span class="text-red-500">*</span></label>
                  <input type="text" id="addUserName" required minlength="2" maxlength="100" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Jan Novák">
                </div>
                <div class="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input type="email" id="addUserEmail" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="jan@firma.cz">
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Telefon</label>
                    <input type="tel" id="addUserPhone" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="+420 XXX XXX XXX">
                  </div>
                </div>
                <div class="mb-2">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Role <span class="text-red-500">*</span></label>
                  <select id="addUserRole" required class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white">
                    <option value="sales">Obchodník</option>
                    <option value="admin">Administrátor</option>
                  </select>
                </div>
                <p class="text-xs text-gray-500">Po vytvoření se zobrazí dočasné heslo, které je nutné předat uživateli. Heslo se zobrazí pouze jednou.</p>
              </form>
            </div>
            <div class="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button type="button" class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" id="modal-cancel-btn">Zrušit</button>
              <button type="button" class="btn btn-primary" onclick="usersAdmin.addUser()">Vytvořit uživatele</button>
            </div>
          </div>
        </div>
      </div>`;
    document.body.insertAdjacentHTML('beforeend', modalHtml);
  }

  async addUser() {
    const form = document.getElementById('addUserForm');
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    const payload = {
      username: document.getElementById('addUserUsername').value.trim(),
      name: document.getElementById('addUserName').value.trim(),
      email: document.getElementById('addUserEmail').value.trim(),
      phone: document.getElementById('addUserPhone').value.trim(),
      role: document.getElementById('addUserRole').value
    };

    try {
      const response = await fetch(`${this.baseUrl}/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (response.ok && result.success) {
        new TailwindModal('addUserModal').hide();
        const tempPassword = result.data?.tempPassword;
        await this.loadUsers();
        this.renderUsers();
        this.showTempPasswordDialog(tempPassword, payload.username, 'Uživatel byl vytvořen');
      } else {
        this.showError(result.message || 'Nepodařilo se vytvořit uživatele');
      }
    } catch (error) {
      console.error('Chyba při vytváření uživatele:', error);
      this.showError('Chyba spojení se serverem');
    }
  }

  // ============================================================
  // Úprava uživatele
  // ============================================================
  editUser(userId) {
    const user = this.users.find(u => String(u.id) === String(userId));
    if (!user) {
      this.showError('Uživatel nebyl nalezen.');
      return;
    }
    if (!document.getElementById('editUserModal')) {
      this.createEditUserModal();
    }
    document.getElementById('editUserId').value = user.id;
    document.getElementById('editUserName').value = user.name || '';
    document.getElementById('editUserEmail').value = user.email || '';
    document.getElementById('editUserPhone').value = user.phone || '';
    document.getElementById('editUserRole').value = user.role || 'sales';
    new TailwindModal('editUserModal').show();
  }

  createEditUserModal() {
    const modalHtml = `
      <div class="fixed inset-0 z-50 overflow-y-auto hidden" id="editUserModal" aria-labelledby="editUserModalLabel" aria-hidden="true">
        <div class="flex items-center justify-center min-h-screen px-4">
          <div class="fixed inset-0 bg-black opacity-50" id="modal-backdrop"></div>
          <div class="bg-white rounded-lg shadow-xl max-w-lg w-full relative">
            <div class="px-6 py-4 border-b border-gray-200">
              <h3 class="text-lg font-medium text-gray-900" id="editUserModalLabel">Upravit uživatele</h3>
              <button type="button" class="absolute top-4 right-4 text-gray-400 hover:text-gray-600" id="modal-close-btn">
                ${icon('x-mark', 'h-6 w-6')}
              </button>
            </div>
            <div class="px-6 py-4">
              <form id="editUserForm">
                <input type="hidden" id="editUserId">
                <div class="mb-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Celé jméno <span class="text-red-500">*</span></label>
                  <input type="text" id="editUserName" required minlength="2" maxlength="100" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                </div>
                <div class="grid grid-cols-2 gap-3 mb-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <input type="email" id="editUserEmail" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-2">Telefon</label>
                    <input type="tel" id="editUserPhone" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm">
                  </div>
                </div>
                <div class="mb-2">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Role <span class="text-red-500">*</span></label>
                  <select id="editUserRole" required class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-white">
                    <option value="sales">Obchodník</option>
                    <option value="admin">Administrátor</option>
                  </select>
                </div>
              </form>
            </div>
            <div class="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button type="button" class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" id="modal-cancel-btn">Zrušit</button>
              <button type="button" class="btn btn-primary" onclick="usersAdmin.updateUser()">Uložit změny</button>
            </div>
          </div>
        </div>
      </div>`;
    document.body.insertAdjacentHTML('beforeend', modalHtml);
  }

  async updateUser() {
    const form = document.getElementById('editUserForm');
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    const userId = document.getElementById('editUserId').value;
    const payload = {
      name: document.getElementById('editUserName').value.trim(),
      email: document.getElementById('editUserEmail').value.trim(),
      phone: document.getElementById('editUserPhone').value.trim(),
      role: document.getElementById('editUserRole').value
    };

    try {
      const response = await fetch(`${this.baseUrl}/users/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (response.ok && result.success) {
        new TailwindModal('editUserModal').hide();
        this.showSuccess('Uživatel byl aktualizován.');
        await this.loadUsers();
        this.renderUsers();
      } else {
        this.showError(result.message || 'Nepodařilo se aktualizovat uživatele');
      }
    } catch (error) {
      console.error('Chyba při aktualizaci uživatele:', error);
      this.showError('Chyba spojení se serverem');
    }
  }

  // ============================================================
  // Reset hesla
  // ============================================================
  async resetPassword(userId) {
    const user = this.users.find(u => String(u.id) === String(userId));
    if (!user) {
      this.showError('Uživatel nebyl nalezen.');
      return;
    }
    if (!confirm(`Opravdu chcete resetovat heslo uživateli „${user.name}"? Bude vygenerováno nové dočasné heslo.`)) {
      return;
    }
    try {
      const response = await fetch(`${this.baseUrl}/users/${userId}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...this.getAuthHeaders() },
        credentials: 'include'
      });
      const result = await response.json();
      if (response.ok && result.success) {
        await this.loadUsers();
        this.renderUsers();
        this.showTempPasswordDialog(result.data?.tempPassword, user.username, 'Heslo bylo resetováno');
      } else {
        this.showError(result.message || 'Nepodařilo se resetovat heslo');
      }
    } catch (error) {
      console.error('Chyba při resetování hesla:', error);
      this.showError('Chyba spojení se serverem');
    }
  }

  // ============================================================
  // Smazání uživatele
  // ============================================================
  async deleteUser(userId) {
    const user = this.users.find(u => String(u.id) === String(userId));
    if (!user) {
      this.showError('Uživatel nebyl nalezen.');
      return;
    }
    if (!confirm(`Opravdu chcete smazat uživatele „${user.name}" (${user.username})?`)) {
      return;
    }
    try {
      const response = await fetch(`${this.baseUrl}/users/${userId}`, {
        method: 'DELETE',
        headers: { ...this.getAuthHeaders() },
        credentials: 'include'
      });
      const result = await response.json();
      if (response.ok && result.success) {
        this.showSuccess('Uživatel byl smazán.');
        await this.loadUsers();
        this.renderUsers();
      } else {
        // Chybové message z API (např. "Nelze smazat posledního administrátora")
        this.showError(result.message || 'Nepodařilo se smazat uživatele');
      }
    } catch (error) {
      console.error('Chyba při mazání uživatele:', error);
      this.showError('Chyba spojení se serverem');
    }
  }

  // ============================================================
  // Dialog s vygenerovaným dočasným heslem (jednorázové zobrazení)
  // ============================================================
  showTempPasswordDialog(tempPassword, username, title) {
    if (!tempPassword) {
      this.showSuccess(`${title}.`);
      return;
    }
    const existing = document.getElementById('tempPasswordModal');
    if (existing) existing.remove();

    const safePwd = this.escapeHtml(tempPassword);
    const safeUser = this.escapeHtml(username || '');
    const modalHtml = `
      <div class="fixed inset-0 z-[70] overflow-y-auto hidden" id="tempPasswordModal" aria-hidden="true">
        <div class="flex items-center justify-center min-h-screen px-4">
          <div class="fixed inset-0 bg-black opacity-50" id="modal-backdrop"></div>
          <div class="bg-white rounded-lg shadow-xl max-w-md w-full relative">
            <div class="px-6 py-4 border-b border-gray-200">
              <h3 class="text-lg font-medium text-gray-900">${this.escapeHtml(title)}</h3>
            </div>
            <div class="px-6 py-4">
              <p class="text-sm text-gray-600 mb-3">Dočasné heslo pro uživatele <strong>${safeUser}</strong>:</p>
              <div class="flex items-center gap-2 mb-3">
                <input type="text" id="tempPasswordValue" readonly value="${safePwd}" class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm font-mono text-sm bg-gray-50 select-all">
                <button type="button" class="btn btn-secondary shrink-0" onclick="usersAdmin.copyTempPassword()">Kopírovat</button>
              </div>
              <div class="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-800">
                Heslo se zobrazí jen jednou, předejte ho uživateli. Po prvním přihlášení si jej bude muset změnit.
              </div>
            </div>
            <div class="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button type="button" class="btn btn-primary" id="modal-cancel-btn">Hotovo</button>
            </div>
          </div>
        </div>
      </div>`;
    document.body.insertAdjacentHTML('beforeend', modalHtml);
    new TailwindModal('tempPasswordModal').show();
  }

  copyTempPassword() {
    const input = document.getElementById('tempPasswordValue');
    if (!input) return;
    const value = input.value;
    const done = () => this.showSuccess('Heslo zkopírováno do schránky.');
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(value).then(done).catch(() => {
        input.select();
        document.execCommand('copy');
        done();
      });
    } else {
      input.select();
      document.execCommand('copy');
      done();
    }
  }

  showSuccess(message) {
    if (typeof window.showNotification === 'function') {
      window.showNotification(message, 'success');
    }
  }

  showError(message) {
    if (typeof window.showNotification === 'function') {
      window.showNotification(message, 'error');
    } else {
      alert(message);
    }
  }
}

export { UsersAdmin };
